// Per-garment "Custom Style Codes" editor — used inside Jacket/Pant/Vest/Shirt
// styling sections in POS and Quick Order. Lets the operator pick a factory code
// (filtered to the relevant garment bucket) and rename it before adding to the
// order. The picked codes ride along with the styling state and show up in the
// submitted order so the factory sees both the original code and the custom label.
//
// value:    [{ id, code, label }]
// onChange: (newArray) => void
// bucket:   "jacket" | "pant" | "vest" | "shirt"

import { useMemo, useState, useRef, useEffect } from "react";
import { Plus, X, Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RC_CODE_LOOKUP } from "@/data/rcCodeLookup";
import { overridesLoaded, subscribeOverrides, getAllOverrides, loadOverrides } from "@/data/rcCodeOverrides";

const BUCKET_PREFIX = {
  jacket: /^C-/i,
  pant:   /^T-/i,
  vest:   /^V-/i,
  shirt:  /^S-/i,
};

const BUCKET_FIELD_PREFIX = {
  jacket: (f) => !f.startsWith("pant_") && !f.startsWith("vest_") && !f.startsWith("shirt_"),
  pant:   (f) => f.startsWith("pant_"),
  vest:   (f) => f.startsWith("vest_"),
  shirt:  (f) => f.startsWith("shirt_"),
};

function buildAllCodesForBucket(bucket, dynamicMap) {
  const codePrefix = BUCKET_PREFIX[bucket];
  const fieldPredicate = BUCKET_FIELD_PREFIX[bucket];
  const seen = new Set();
  const out = [];
  // 1. Static base lookup
  for (const [field, valueMap] of Object.entries(RC_CODE_LOOKUP || {})) {
    if (!valueMap || typeof valueMap !== "object") continue;
    if (fieldPredicate && !fieldPredicate(field)) continue;
    for (const [value, code] of Object.entries(valueMap)) {
      if (!code || seen.has(code)) continue;
      if (codePrefix && !codePrefix.test(code)) continue;
      seen.add(code);
      out.push({ code, label: value, field });
    }
  }
  // 2. Admin-added / overridden codes from the live rc_codes collection
  if (dynamicMap) {
    for (const [field, valueMap] of Object.entries(dynamicMap)) {
      if (fieldPredicate && !fieldPredicate(field)) continue;
      for (const [value, code] of Object.entries(valueMap)) {
        if (!code || seen.has(code)) continue;
        if (codePrefix && !codePrefix.test(code)) continue;
        seen.add(code);
        out.push({ code, label: value, field });
      }
    }
  }
  return out.sort((a, b) => a.code.localeCompare(b.code));
}

export default function CustomStyleCodes({
  value = [],
  onChange,
  bucket = "jacket",
  testId = "custom-codes",
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    // Kick the loader so the picker sees admin-added codes even before any other
    // page on the site has triggered it.
    if (!overridesLoaded()) loadOverrides();
    return subscribeOverrides(() => tick((n) => n + 1));
  }, []);
  const allCodes = useMemo(
    () => buildAllCodesForBucket(bucket, overridesLoaded() ? getAllOverrides() : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bucket, overridesLoaded()],
  );
  const [adding, setAdding] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [label, setLabel] = useState("");
  const pickerRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allCodes.slice(0, 60);
    return allCodes.filter((c) =>
      c.code.toLowerCase().includes(needle) ||
      c.label.toLowerCase().includes(needle) ||
      c.field.toLowerCase().includes(needle),
    ).slice(0, 60);
  }, [allCodes, q]);

  const resetForm = () => {
    setAdding(false);
    setPickerOpen(false);
    setQ("");
    setPicked(null);
    setLabel("");
  };

  const add = () => {
    if (!picked) return;
    const id = `${picked.code}-${Date.now()}`;
    onChange?.([
      ...value,
      { id, code: picked.code, label: label.trim() || picked.label },
    ]);
    resetForm();
  };

  const remove = (id) => {
    onChange?.(value.filter((v) => v.id !== id));
  };

  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-400">
          Custom Style Codes{value.length > 0 && <span className="text-amber-400 ml-1.5">({value.length})</span>}
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-amber-500/10 transition-colors"
            data-testid={`${testId}-add-btn`}
          >
            <Plus size={11} /> Add code
          </button>
        )}
      </div>

      {/* Existing custom codes */}
      {value.length > 0 && (
        <div className="space-y-1 mb-2">
          {value.map((it) => (
            <div
              key={it.id}
              className="flex items-center gap-2 bg-white/5 rounded px-2 py-1.5 text-xs"
              data-testid={`${testId}-item-${it.code}`}
            >
              <span className="flex-1 truncate text-slate-200">{it.label}</span>
              <code className="text-[10px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded shrink-0">
                {it.code}
              </code>
              <button
                type="button"
                onClick={() => remove(it.id)}
                className="text-slate-500 hover:text-red-400 shrink-0"
                data-testid={`${testId}-remove-${it.code}`}
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div className="bg-[#0a1628] border border-white/10 rounded p-2 space-y-2">
          {/* Code picker */}
          <div className="relative" ref={pickerRef}>
            <label className="text-[10px] text-slate-400 block mb-1">Pick a factory code</label>
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className="w-full bg-[#111c2e] border border-white/10 rounded h-8 px-2 text-xs flex items-center gap-1.5 hover:border-amber-500/40 transition-colors"
              data-testid={`${testId}-picker-trigger`}
            >
              <Search size={11} className="text-slate-500 shrink-0" />
              <span className="flex-1 text-left truncate">
                {picked
                  ? <>{picked.label}{" "}<code className="text-amber-400 ml-1">{picked.code}</code></>
                  : <span className="text-slate-500">Search code or label…</span>}
              </span>
            </button>
            {pickerOpen && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-[#0a1628] border border-white/10 rounded shadow-xl max-h-64 overflow-hidden flex flex-col">
                <div className="p-1.5 border-b border-white/5">
                  <Input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={`Search ${allCodes.length} ${bucket} codes…`}
                    className="bg-[#111c2e] border-white/10 h-7 text-xs"
                    data-testid={`${testId}-picker-search`}
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filtered.length === 0 && (
                    <div className="px-2 py-2 text-[11px] text-slate-500">No matches</div>
                  )}
                  {filtered.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => {
                        setPicked(c);
                        setPickerOpen(false);
                        if (!label) setLabel(c.label);
                      }}
                      className="w-full px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-white/5 text-left"
                      data-testid={`${testId}-picker-opt-${c.code}`}
                    >
                      <span className="flex-1 truncate">
                        {c.label}
                        <span className="text-slate-500 ml-1 text-[10px]">{c.field}</span>
                      </span>
                      <code className="text-[10px] text-amber-400 shrink-0">{c.code}</code>
                      {picked?.code === c.code && <Check size={11} className="text-amber-400" />}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Rename */}
          <div>
            <label className="text-[10px] text-slate-400 block mb-1">Display label (optional rename)</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={picked ? picked.label : "e.g. Custom Lapel"}
              disabled={!picked}
              className="bg-[#111c2e] border-white/10 h-7 text-xs"
              data-testid={`${testId}-label-input`}
            />
          </div>

          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={resetForm}
              className="h-7 text-xs px-2"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={add}
              disabled={!picked}
              className="h-7 text-xs px-2 bg-amber-500 hover:bg-amber-600 text-black"
              data-testid={`${testId}-save-btn`}
            >
              <Plus size={11} className="mr-0.5" /> Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
